import { ReactNode } from "react";
import { SerializedStyles, css, useTheme } from "@emotion/react";
import { h1, h2, h3, h4, h5, h6, mediaQueries, pxToRem } from "../styles";
import { UpsunTheme } from "../themes"

import {
  NeutralIconActive,
  NeutralIconWeakDefault,
  CanvasDefault,
} from "../../tokens/js/light"

import {
  NeutralIconActive as NeutralIconActiveDark,
  NeutralIconWeakDefault as NeutralIconWeakDefaultDark,
  CanvasDefault as CanvasDefaultDark,
} from "../../tokens/js/dark"

import { ReactComponent as LinkIcon } from "../icons/link.svg"

type HeadingPropsChildren = {
  level: number,
  levelStyle?: number,
  text?: ReactNode,
  children: ReactNode,
  css?: SerializedStyles | SerializedStyles[]
  id?: String
  jumpLink?: boolean
}

type HeadingPropsText = {
  level: number,
  levelStyle?: number,
  text: string,
  children?: string,
  css?: SerializedStyles | SerializedStyles[]
  id?: String
  jumpLink?: boolean
}

type HeadingProps = HeadingPropsChildren | HeadingPropsText;

const Heading = ({ 
  level, 
  levelStyle, 
  text, 
  children, 
  jumpLink, 
  ...props
} : HeadingProps) : JSX.Element => {
  const theme = useTheme() as UpsunTheme;

  const forceValidHeadingLevel = (n: number) => {
    if (!Number.isInteger(level) || n < 1 || n > 6) {
      return 6
    }
    return n
  }

  const headingLevel = forceValidHeadingLevel(level);
  const HeadingTag : any = `h${headingLevel}`;
  const styleLevel = (levelStyle && forceValidHeadingLevel(levelStyle)) ?? headingLevel;
  const styles = [h1, h2, h3, h4, h5, h6];
  const myStyle = styles[styleLevel - 1];

  const shouldRenderJumplink = jumpLink && (
    headingLevel > 1 || styleLevel > 1
  );

  const copyIdToClipboard = (e: any) => {
    e.preventDefault();

    if (navigator) {
      let linkElement;
      if (e.target.tagName === "svg") {
        linkElement = e.target.parentElement;
      } else if(e.target.tagName === "A") {
        linkElement = e.target;
      }

      if (linkElement) {
        navigator.clipboard.writeText(linkElement.href);
      }
    }
  };

  const jumplinkSize = 24;
  const iconSize = 16;
  const topMargin = (styleLevel <= 3) ? -4 : -2;

  const jumpLinkStyles = css`
    display: none;
    color: inherit;
    
    ${mediaQueries.xl} {
      display: inline-flex;
      justify-content: center;
      align-items: center;
      position: relative;
      left: ${pxToRem(4)};
      top: ${pxToRem(topMargin)};
      width: ${pxToRem(jumplinkSize)};
      height: ${pxToRem(jumplinkSize)};
      border-radius: 50%;
      background-color: ${theme.name == 'light' ? NeutralIconWeakDefault : NeutralIconWeakDefaultDark};
    }

    &:hover {
      background-color: ${theme.name == 'light' ? NeutralIconActive : NeutralIconActiveDark};
      transition: 0.3s ease all;
    }

    svg {
      width: ${pxToRem(iconSize)};
      height: ${pxToRem(iconSize)};
    }

    path {
      fill: ${theme.name == 'light' ? CanvasDefault : CanvasDefaultDark};
    }
  `;

  // @todo make sure headings can render italics, sub, sup
  return (
    <HeadingTag css={myStyle} {...props}>
      {children || text}
      {shouldRenderJumplink && props.id && (
        <a 
          css={jumpLinkStyles} 
          href={`#${props.id}`} 
          title="Link to this heading" 
          onClick={(e) => copyIdToClipboard(e)}
        >
          <LinkIcon />
        </a>
      )}
    </HeadingTag>
  )
};

export default Heading;
